import { all, put, call, takeLatest } from 'redux-saga/effects'
import axios from 'portal/utils/axios-msal'

import {
  wmrConfigLoadSuccess,
  wmrConfigLoadFailure,
  WMR_CONFIG_LOAD,
  WmrConfigLoadAction,
  wmrRulesetUploadSuccess,
  wmrRulesetUploadFailure,
  WMR_RULESET_UPLOAD,
  WmrRulesetUploadAction,
  wmrRulesetConfirmSuccess,
  wmrRulesetConfirmFailure,
  WMR_RULESET_CONFIRM,
  WmrRulesetConfirmAction,
  wmrRulesetExportSuccess,
  wmrRulesetExportFailure,
  WMR_RULESET_EXPORT,
  WmrRulesetExportAction,
  WmrConfigData,
  WmrUploadResponse
} from './actions'

function* wmrConfigLoadSaga(action: WmrConfigLoadAction) {
  const { accountId, projectId } = action

  try {
    const response: { data: WmrConfigData } = yield call(() =>
      axios.get(`/accounts/${accountId}/projects/${projectId}/wmr-config`, {})
    )

    const config: WmrConfigData =
      response && response.data
        ? response.data
        : {
            templateSasUrl: '',
            activeRuleset: {
              ruleCount: 0,
              isReprocessing: false
            }
          }

    yield put(wmrConfigLoadSuccess(accountId, projectId, config))
  } catch (error) {
    console.error(error)
    yield put(wmrConfigLoadFailure(accountId, projectId, error))
  }
}

function* wmrRulesetUploadSaga(action: WmrRulesetUploadAction) {
  const { accountId, projectId, file } = action

  try {
    const formData = new FormData()
    formData.append('file', file)

    const response: {
      data: WmrUploadResponse
    } = yield call(() =>
      axios.post(
        `/accounts/${accountId}/projects/${projectId}/wmr-config/rulesets`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    )

    const uploadResponse: WmrUploadResponse = {
      rulesetId: response.data.rulesetId,
      ruleCount: response.data.ruleCount,
      isValid: response.data.isValid,
      validationResults: response.data.validationResults
    }

    yield put(wmrRulesetUploadSuccess(accountId, projectId, uploadResponse))
  } catch (error: any) {
    // Handle 422 Unprocessable Entity - validation errors
    // These should open the validation modal, not show as a failure
    if (error?.response?.status === 422 && error?.response?.data) {
      const uploadResponse: WmrUploadResponse = {
        rulesetId: error.response.data.rulesetId || null,
        ruleCount: error.response.data.ruleCount || 0,
        isValid: error.response.data.isValid || false,
        validationResults: error.response.data.validationResults || []
      }

      yield put(wmrRulesetUploadSuccess(accountId, projectId, uploadResponse))
    } else if (error?.response?.status === 400) {
      // Handle 400 Bad Request - invalid file format
      console.log('File format error:', error.response)
      const formatError = new Error(
        `${error.response.data?.title} ${error.response.data?.errors?.file[0]}`
      )
      yield put(wmrRulesetUploadFailure(accountId, projectId, formatError))
    } else if (error?.response?.status === 409) {
      // Handle 409 Conflict - configuration is being processed
      const conflictError = new Error(
        'A configuration is currently being processed. Please wait until processing completes before uploading a new configuration.'
      )
      yield put(wmrRulesetUploadFailure(accountId, projectId, conflictError))
    } else {
      // Other errors are true failures
      yield put(wmrRulesetUploadFailure(accountId, projectId, error))
    }
  }
}

function* wmrRulesetConfirmSaga(action: WmrRulesetConfirmAction) {
  const { accountId, projectId, rulesetId } = action

  try {
    yield call(() =>
      axios.put(
        `/accounts/${accountId}/projects/${projectId}/wmr-config/rulesets/${rulesetId}/confirm`
      )
    )

    yield put(wmrRulesetConfirmSuccess(accountId, projectId, rulesetId))
  } catch (error: any) {
    console.error(error)

    // Handle 409 Conflict - configuration is being processed
    if (error?.response?.status === 409) {
      const conflictError = new Error(
        'A configuration is currently being processed. Please wait until processing completes before confirming a new configuration.'
      )
      yield put(
        wmrRulesetConfirmFailure(accountId, projectId, rulesetId, conflictError)
      )
    } else {
      yield put(
        wmrRulesetConfirmFailure(accountId, projectId, rulesetId, error)
      )
    }
  }
}

function* wmrRulesetExportSaga(action: WmrRulesetExportAction) {
  const { accountId, projectId } = action

  try {
    const response: { data: { exportFileSasUrl: string } } = yield call(() =>
      axios.get(
        `/accounts/${accountId}/projects/${projectId}/wmr-config/export-current`,
        {}
      )
    )

    const exportFileSasUrl: string =
      response && response.data ? response.data.exportFileSasUrl : ''

    yield put(wmrRulesetExportSuccess(accountId, projectId, exportFileSasUrl))
  } catch (error) {
    console.error(error)
    yield put(wmrRulesetExportFailure(accountId, projectId, error))
  }
}

export function* watch() {
  yield all([
    takeLatest(WMR_CONFIG_LOAD, wmrConfigLoadSaga),
    takeLatest(WMR_RULESET_UPLOAD, wmrRulesetUploadSaga),
    takeLatest(WMR_RULESET_CONFIRM, wmrRulesetConfirmSaga),
    takeLatest(WMR_RULESET_EXPORT, wmrRulesetExportSaga)
  ])
}
